import { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { format, parse } from "date-fns";
import { CalendarIcon, Clock, ChevronUp, ChevronDown } from "lucide-react";

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  disablePast?: boolean;
  className?: string;
  "data-testid"?: string;
}

export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  disabled = false,
  disablePast = false,
  className,
  "data-testid": testId,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);

  const selectedDate = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;
  const isValidDate = selectedDate && !isNaN(selectedDate.getTime());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal glass-input",
            !value && "text-muted-foreground",
            className
          )}
          data-testid={testId}
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-primary/60" />
          {isValidDate ? format(selectedDate, "MMM d, yyyy") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 glass-dialog" align="start">
        <Calendar
          mode="single"
          selected={isValidDate ? selectedDate : undefined}
          onSelect={(date) => {
            if (date) {
              onChange(format(date, "yyyy-MM-dd"));
              setOpen(false);
            }
          }}
          disabled={disablePast ? (date) => date < new Date(new Date().setHours(0, 0, 0, 0)) : undefined}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  "data-testid"?: string;
}

export function TimePicker({
  value,
  onChange,
  disabled = false,
  className,
  "data-testid": testId,
}: TimePickerProps) {
  const [open, setOpen] = useState(false);

  const parts = value ? value.split(":") : ["12", "00"];
  let hour24 = parseInt(parts[0]) || 12;
  const minute = parseInt(parts[1]) || 0;
  const isPM = hour24 >= 12;
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;

  function updateTime(h12: number, m: number, pm: boolean) {
    let h24 = h12;
    if (pm && h12 !== 12) h24 = h12 + 12;
    if (!pm && h12 === 12) h24 = 0;
    onChange(`${String(h24).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }

  function stepHour(dir: number) {
    let newH = hour12 + dir;
    let newPM = isPM;
    if (newH > 12) { newH = 1; }
    if (newH < 1) { newH = 12; }
    if ((hour12 === 11 && dir === 1) || (hour12 === 12 && dir === -1)) {
      newPM = !isPM;
    }
    updateTime(newH, minute, newPM);
  }

  function stepMinute(dir: number) {
    let newM = minute + dir * 5;
    if (newM >= 60) newM = 0;
    if (newM < 0) newM = 55;
    updateTime(hour12, newM, isPM);
  }

  function toggleAmPm() {
    updateTime(hour12, minute, !isPM);
  }

  const displayTime = `${hour12}:${String(minute).padStart(2, "0")} ${isPM ? "PM" : "AM"}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "justify-start text-left font-normal glass-input",
            !value && "text-muted-foreground",
            className
          )}
          data-testid={testId}
        >
          <Clock className="mr-2 h-4 w-4 text-primary/60" />
          {value ? displayTime : "Pick time"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4 glass-dialog" align="start">
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-center">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => stepHour(1)}
              data-testid="button-hour-up"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <div className="text-2xl font-semibold tabular-nums w-10 text-center" data-testid="text-hour">
              {hour12}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => stepHour(-1)}
              data-testid="button-hour-down"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>

          <span className="text-2xl font-semibold">:</span>

          <div className="flex flex-col items-center">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => stepMinute(1)}
              data-testid="button-minute-up"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <div className="text-2xl font-semibold tabular-nums w-10 text-center" data-testid="text-minute">
              {String(minute).padStart(2, "0")}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => stepMinute(-1)}
              data-testid="button-minute-down"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex flex-col items-center ml-2">
            <Button
              type="button"
              variant={isPM ? "ghost" : "default"}
              size="sm"
              className={cn("text-xs h-7 w-10", !isPM && "bg-primary text-primary-foreground")}
              onClick={() => { if (isPM) toggleAmPm(); }}
              data-testid="button-am"
            >
              AM
            </Button>
            <Button
              type="button"
              variant={isPM ? "default" : "ghost"}
              size="sm"
              className={cn("text-xs h-7 w-10 mt-1", isPM && "bg-primary text-primary-foreground")}
              onClick={() => { if (!isPM) toggleAmPm(); }}
              data-testid="button-pm"
            >
              PM
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
